/**
 * TenantAiSettingsPanel — per-tenant credit management.
 *
 * Ported from SuperAdminAIStatus.tsx:1944-2199 (credit management). The old
 * per-tenant "AI usage limits" (media-service /admin/ai/tenant/{t}/limits) was
 * removed with the old image pipeline (Task 20, 2026-10-04) — nothing enforced
 * it; spend is governed by Ruby Tokens. Corrections applied on the port:
 *
 *  - The tenant list comes from the FULL tenant directory, not from
 *    usageReport.byTenant. The old page could only manage credits for a tenant
 *    that happened to appear in the media usage report, which silently hid
 *    every other tenant.
 *  - All inputs and buttons carry min-h-[44px].
 *
 * Task 28: the Creator Studio dollar wallet is retired as a way to pay — word
 * art, menu boards, compositions and videos charge Ruby Tokens. The admin load,
 * the charging toggle and the price controls are gone (the stored fields are
 * kept). The balance stays visible READ-ONLY, with its Ruby Token equivalent;
 * it is never converted automatically — what happens to it awaits an owner decision.
 */
import React, { useEffect, useState } from 'react';
import { Building2, ChevronRight, Coins } from 'lucide-react';
import { api } from '../../../services/apiService';
import type { AiTabProps } from '../AiControlCenter';
import { RubyGemMark } from '../../../components/RubyGemMark';
import { creditsToRubyTokens } from '../rubyAi/units';


interface TenantRow { tenantId: string; businessName: string }

interface Selected {
  tenantId: string;
  businessName: string;
  /** Creator Studio wallet balance, in cents. */
  balance: number;
  transactions: Array<{ transactionId: string; type: string; amount: number }>;
}

const INPUT =
  'w-full px-3 py-2 min-h-[44px] text-sm border border-stone-200 rounded-lg bg-white text-stone-800 focus:outline-none focus:ring-2 focus:ring-rose-500/50';

const TenantAiSettingsPanel: React.FC<{ showToast: AiTabProps['showToast'] }> = ({ showToast }) => {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Selected | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const all = await api.tenants.getAll();
        if (!cancelled) {
          setTenants((all || []).map((t: any) => ({
            tenantId: t.tenantId || t.id,
            businessName: t.businessName || t.name || t.tenantId || t.id,
          })));
          setListError(null);
        }
      } catch (err: any) {
        if (!cancelled) setListError(err?.message || 'Tenant directory unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = query
    ? tenants.filter((t) =>
        t.businessName.toLowerCase().includes(query.toLowerCase()) ||
        t.tenantId.toLowerCase().includes(query.toLowerCase()))
    : [];

  const selectTenant = async (tenantId: string) => {
    setBusy(true);
    try {
      const [credits, tenant] = await Promise.all([
        api.superAdmin.getTenantCredits(tenantId),
        api.superAdmin.getTenant(tenantId).catch(() => null),
      ]);
      const c = credits as any;
      // GET /admin/tenants/{id}/credits answers { wallet: { balance, … }, transactions }.
      const wallet = c?.wallet ?? c ?? {};
      setSelected({
        tenantId,
        businessName: (tenant as any)?.businessName || c?.tenant?.businessName || tenantId,
        balance: Number(wallet.balance) || 0,
        transactions: c?.transactions || [],
      });
    } catch (err: any) {
      showToast(err?.message || 'Failed to load tenant credits', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Coins size={14} className="text-blue-500" /> Creator Studio balances
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Read-only. Creator Studio now charges Ruby Tokens; this old wallet is no longer spent.
          </p>
        </div>
        {busy && <RubyGemMark size={16} drawing />}
      </div>

      <div className="p-4 space-y-5">
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 block">Select tenant</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by business name or tenant ID…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`${INPUT} pr-10`}
            />
            <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
          {listError && <p className="text-[11px] text-gray-500">{listError}</p>}

          {query && filtered.length > 0 && !selected && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-stone-200 bg-white">
              {filtered.slice(0, 10).map((t) => (
                <button
                  key={t.tenantId}
                  type="button"
                  onClick={() => void selectTenant(t.tenantId)}
                  className="w-full px-4 py-3 min-h-[44px] text-left hover:bg-stone-50 flex items-center justify-between border-b border-stone-100 last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium text-stone-800">{t.businessName}</p>
                    <p className="text-[11px] text-gray-500">{t.tenantId}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              ))}
            </div>
          )}
          {query && filtered.length === 0 && !selected && (
            <p className="text-[11px] text-gray-500">No tenant matches “{query}”.</p>
          )}
        </div>

        {selected ? (
          <div className="space-y-5 pt-4 border-t border-stone-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-stone-800">{selected.businessName}</h4>
                <p className="text-[11px] text-gray-500">{selected.tenantId}</p>
              </div>
              <button
                type="button"
                onClick={() => { setSelected(null); setQuery(''); }}
                className="min-h-[44px] px-2 text-xs text-gray-500 hover:text-gray-700"
              >
                Change tenant
              </button>
            </div>

            <div className="p-4 bg-gradient-to-r from-blue-50 to-rose-50 rounded-xl">
              <p className="text-[11px] text-gray-500 mb-1">
                Creator Studio balance — retired, awaiting an owner decision (not raised automatically yet)
              </p>
              <p className="text-2xl font-bold text-blue-600">${(selected.balance / 100).toFixed(2)}</p>
              <p className="text-[11px] text-gray-500 mt-1">
                ≈ {creditsToRubyTokens(selected.balance).toLocaleString()} Ruby Tokens.
                Nothing is converted automatically.
              </p>
            </div>

            {selected.transactions.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-xs font-medium text-gray-700">Recent transactions</h5>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {selected.transactions.slice(0, 10).map((txn) => (
                    <div key={txn.transactionId} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg text-xs">
                      <span className="text-stone-600 capitalize">{txn.type.replace('_', ' ')}</span>
                      <span className={`font-medium ${txn.type === 'spend' ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {txn.type === 'spend' ? '-' : '+'}${(Math.abs(txn.amount) / 100).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-500 text-center py-8">
            <Building2 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p>Search for a tenant above to see their Creator Studio balance</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default TenantAiSettingsPanel;
